/**
 * Fix default values service.
 * Fixed default values provided to prevent mismatch with data from api calls.
 * @module fixDefaults
 */

/**
 * Check if currency provided in default values exist in data
 * @param {object} - data, currency name to check
 * @return {boolean} -
 */
const checkCurrencyExistance = ({ data, currency }) => (data[currency] ? true : false);

/**
 * Get first currency from array
 * @param {array} currencyList - array of currencies available
 * @return {string} - first currency from the array provided
 */
const getFirstCurrencyFromList = (currencyList) => currencyList[0];

/**
 * Get minimal value for amount or term
 * @param {object} data - data
 * @param {string} itemName - type of item, term or amount
 * @return {number} - min value
 */
const getMinTermOrAmount = (data, itemName) => data[getFirstCurrencyFromList(Object.keys(data))][itemName].min;

/**
 * Prepare default values:
 * - transform keys to acceptable format
 * - set dateStart if no date provided or provided date is invalid
 * - set refill property to match isRefillable deposit property
 * @param {object} - consists of default values and refillable
 * @return {object} - default values object, transformed and prepared
 */
const fixDefaults = (toBeFixed) => {
    const { data, defaultValues, isRefillable, isTermInDays } = toBeFixed;
    const currency = checkCurrencyExistance({ data, currency: defaultValues.currency })
        ? defaultValues.currency
        : getFirstCurrencyFromList(Object.keys(data));
    const dateStartObj = new Date(defaultValues.dateStart);
    const termMin = getMinTermOrAmount(data, 'term');

    if (defaultValues.amount === null) {
        defaultValues.amount = getMinTermOrAmount(data, 'amount');
    }

    if (defaultValues.term === null) {
        defaultValues.term = isTermInDays ? termMin : termMin.termMinInDays;
    } else {
        const terms = data[currency].term;
        let termMinName;
        let termMaxName;

        if (!isTermInDays) {
            termMinName = 'termMinInDays';
            termMaxName = 'termMaxInDays';
        } else {
            termMinName = 'min';
            termMaxName = 'max';
        }

        if (
            defaultValues.term < terms[termMinName] ||
            defaultValues.term > terms[termMaxName] ||
            !terms.values.some((item) => defaultValues.term === item[termMinName])
        ) {
            defaultValues.term = termMin[termMinName];
        }
    }

    if (defaultValues.capitalized === null) {
        defaultValues.capitalized = false;
    }

    // if default date equals an empty string or any other non valid data format
    if (!defaultValues.dateStart || isNaN(dateStartObj.getDate())) {
        defaultValues.dateStart = new Date();
    } else {
        defaultValues.dateStart = dateStartObj;
    }

    // deposit can be non refillable, but it may happen that refill value will be set in defaultValues
    // and if so we reset it to ensure that engine will process rates correctly
    if ((isRefillable && defaultValues.refill === null) || !isRefillable) {
        defaultValues.refill = 0;
    }

    // this is for engine module to calculate correct rate and and total amount
    defaultValues.termInDays = true;

    if (defaultValues.currency !== currency) {
        defaultValues.currency = currency;
    }

    toBeFixed.userInput = defaultValues;
    delete toBeFixed.defaultValues;

    return toBeFixed;
};

export { checkCurrencyExistance, getFirstCurrencyFromList, getMinTermOrAmount, fixDefaults };
